<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-4xl mx-auto px-4 py-10 w-full">
      <h1 class="text-3xl font-bold mb-8 text-center">Meu Perfil</h1>

      <div class="bg-white shadow-xl rounded-2xl p-6 md:p-10 space-y-8">

        <!-- AVATAR -->
        <div class="flex justify-center">
          <AvatarUploader
            v-model="previewImage"
            @file-selected="file => (attachmentFile = file)"
          />
        </div>

        <!-- CAMPOS -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div>
            <label class="font-medium">Nome</label>
            <input v-model="form.name" class="input-gold mt-1" />
          </div>

          <div>
            <label class="font-medium">Headline</label>
            <input
              v-model="form.headline"
              maxlength="120"
              placeholder="Ex.: Desenvolvedor Full Stack"
              class="input-gold mt-1"
            />
          </div>

          <div class="md:col-span-2">
            <label class="font-medium">Bio</label>
            <textarea
              v-model="form.bio"
              rows="4"
              class="input-gold mt-1"
              placeholder="Conte um pouco sobre você..."
            ></textarea>
          </div>

          <div>
            <label class="font-medium">Nível</label>
            <select v-model="form.experience_level" class="input-gold mt-1">
              <option disabled value="">Selecione</option>
              <option value="junior">Júnior</option>
              <option value="pleno">Pleno</option>
              <option value="senior">Sênior</option>
            </select>
          </div>

          <div>
            <label class="font-medium">Experiência Profissional</label>
            <textarea
              v-model="form.professional_experience"
              rows="4"
              class="input-gold mt-1"
            ></textarea>
          </div>

          <!-- SKILLS -->
          <div class="md:col-span-2">
            <label class="font-medium">Habilidades</label>

            <select
              v-model="selectedSkill"
              @change="addSkill"
              class="input-gold mt-1"
            >
              <option disabled value="">Selecione sua skill</option>
              <option
                v-for="skill in skills"
                :key="skill.id"
                :value="skill.id"
              >
                {{ skill.description }}
              </option>
            </select>

            <div class="flex flex-wrap gap-2 mt-3">
              <div
                v-for="(skill, index) in form.skills"
                :key="index"
                class="bg-[#1a1a1a] text-white px-3 py-1 rounded-full flex items-center gap-2"
              >
                <span class="text-sm">{{ skill.description }}</span>
                <button
                  @click="removeSkill(index)"
                  class="text-red-400 text-sm"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- BOTÃO DE CONFIRMAÇÃO -->
        <div class="flex justify-end">
          <button
            @click="confirmUpdate"
            :disabled="loading"
            class="px-6 py-3 rounded-xl bg-[#1a1a1a] text-white disabled:opacity-40 mt-4"
          >
            {{ loading ? "Atualizando..." : "Atualizar Cadastro" }}
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import api from "@/services/api";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import AvatarUploader from "@/components/AvatarUploader.vue";
import { useRouter } from "vue-router";

const router = useRouter();
const { proxy } = getCurrentInstance();

const skills = ref([]);
const selectedSkill = ref("");
const previewImage = ref(null); // url mostrada no AvatarUploader

const attachmentFile = ref(null);
const loading = ref(false);

const form = ref({
  id: "",
  name: "",
  headline: "",
  bio: "",
  experience_level: "",
  professional_experience: "",
  skills: [],
});

/**
 * Constrói a URL completa usando a variável de ambiente VUE_APP_URL_API_STORAGE.
 * - Se o valor recebido já for uma URL absoluta, retorna ela (após encodeURI).
 * - Se for um caminho relativo (com ou sem backslashes), normaliza e prefixa com a base.
 * - Se não houver base configurada, retorna caminho relativo com "/" prefixado.
 */
function buildStorageUrl(rawPath) {
  if (!rawPath) return null;

  const maybe = String(rawPath);

  // se já for URL completa (http(s):// ou //cdn)
  if (/^(https?:)?\/\//i.test(maybe)) {
    try {
      return encodeURI(maybe);
    } catch {
      return maybe;
    }
  }

  // base da env (Vue CLI)
  const baseRaw = process.env.VUE_APP_URL_API_STORAGE || "";
  const base = String(baseRaw).replace(/\/+$/, ""); // remove slash final

  // normaliza barras e remove barras iniciais
  const normalized = maybe.replace(/\\/g, "/").replace(/^\/+/, "").replace(/\/+/g, "/");

  let final;
  if (base) final = `${base}/${normalized}`;
  else final = `/${normalized}`;

  try {
    return encodeURI(final);
  } catch {
    return final;
  }
}

/**
 * Recebe o campo attachment do usuário e determina a URL do preview.
 * Aceita:
 * - null/undefined
 * - array de strings (['user/.../file.png', ...])
 * - string (caminho único)
 * - objeto { url: '...', ... }
 */
function resolveAttachmentPreview(attachmentField) {
  if (!attachmentField) return null;

  // se já é objeto com url
  if (typeof attachmentField === "object" && !Array.isArray(attachmentField)) {
    if (attachmentField.url) return buildStorageUrl(attachmentField.url);
    // se for {0: 'path', length:1} (pouco provável) ignoramos
  }

  // se for array
  if (Array.isArray(attachmentField) && attachmentField.length > 0) {
    // preferimos o primeiro item
    const first = attachmentField[0];
    return buildStorageUrl(first);
  }

  // se for string (caminho)
  if (typeof attachmentField === "string") {
    return buildStorageUrl(attachmentField);
  }

  return null;
}

onMounted(async () => {
  // checa autenticação mínima (se quiser refinar, mantenha sua lógica)
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  if (!userData || !userData.user?.id) {
    router.push("/");
    return;
  }

  await loadUserData();
  await getSkills();
});

const loadUserData = async () => {
  try {
    const { data } = await api.get("/users/profile");
    const u = data.data || data;

    form.value = {
      id: u.id,
      name: u.name,
      headline: u.headline || "",
      bio: u.bio || "",
      experience_level: u.experience_level || "",
      professional_experience: u.professional_experience || "",
      skills: Array.isArray(u.skills)
        ? u.skills.map((s) =>
            typeof s === "string" ? { id: null, description: s } : s
          )
        : [],
    };

    // Resolve previewImage usando a env VUE_APP_URL_API_STORAGE
    // A API pode retornar:
    // - u.attachment como array de strings
    // - u.attachment como string
    // - u.attachment como objeto { url: '...' }
    // - ou talvez u.attachment seja JSON string já parseada (verificamos)
    let attachmentField = u.attachment;

    // lidar com caso a API devolva attachment como JSON string: '["path"]'
    if (typeof attachmentField === "string") {
      // tentar parsear somente se parece com JSON array
      const maybeTrim = attachmentField.trim();
      if (maybeTrim.startsWith("[") || maybeTrim.startsWith("{")) {
        try {
          attachmentField = JSON.parse(attachmentField);
        } catch {
          // se não parsear, mantemos como string caminho
        }
      }
    }

    const preview = resolveAttachmentPreview(attachmentField);
    if (preview) previewImage.value = preview;

  } catch (error) {
    console.error("Erro ao carregar perfil:", error);
    proxy.$swal.fire({
      icon: "error",
      title: "Erro ao carregar perfil",
    });
  }
};

const getSkills = async () => {
  try {
    const { data } = await api.get("/categories");
    skills.value = data.data || [];
  } catch (err) {
    console.error("Erro ao carregar skills", err);
    proxy.$swal.fire({ icon: "error", title: "Erro ao carregar skills" });
  }
};

const addSkill = () => {
  if (!selectedSkill.value) return;
  const item = skills.value.find((s) => s.id === selectedSkill.value);
  if (!form.value.skills.some((s) => s.id === item.id)) form.value.skills.push(item);

  selectedSkill.value = "";
};

const removeSkill = (i) => form.value.skills.splice(i, 1);

const updateProfile = async () => {
  loading.value = true;

  try {
    const payload = new FormData();
    payload.append("name", form.value.name);
    payload.append("headline", form.value.headline);
    payload.append("bio", form.value.bio);
    payload.append("experience_level", form.value.experience_level);
    payload.append("professional_experience", form.value.professional_experience);

    payload.append("skills", JSON.stringify(form.value.skills));

    if (attachmentFile.value) payload.append("attachment[]", attachmentFile.value);

    await api.post(`/users/${form.value.id}`, payload, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    proxy.$swal.fire({
      icon: "success",
      title: "Perfil atualizado!",
    });

    // após atualização, forçamos reload do perfil para pegar a URL real do servidor
    await loadUserData();
    // limpamos attachmentFile (já que previewImage foi atualizado por loadUserData)
    attachmentFile.value = null;
  } catch (err) {
    console.error("Erro ao atualizar perfil:", err);
    proxy.$swal.fire({
      icon: "error",
      title: "Erro ao atualizar",
      text: err?.response?.data?.error || "Tente novamente.",
    });
  } finally {
    loading.value = false;
  }
};

const confirmUpdate = () => {
  proxy.$swal
    .fire({
      title: "Atualizar Cadastro?",
      text: "Deseja salvar as alterações?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, atualizar",
      cancelButtonText: "Cancelar",
      customClass: {
        confirmButton: "swal2-confirm-btn-custom",
        cancelButton: "swal2-cancel-btn-custom",
      },
    })
    .then((res) => {
      if (res.isConfirmed) updateProfile();
    });
};
</script>

<style>
.swal2-cancel-btn-custom {
  color: #1f2937 !important;
  border: 1px solid #9ca3af !important;
  background-color: #ffffff !important;
}

.swal2-cancel-btn-custom:hover {
  background-color: #f3f4f6 !important;
}

.swal2-confirm-btn-custom {
  color: #ffffff !important;
  /* ← TEXTO BRANCO */
  background-color: #1a1a1a !important;
  border: none !important;
}

.swal2-confirm-btn-custom:hover {
  background-color: #000000 !important;
}
</style>
